import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { fetchBookings } from 'src/features/bookings/api/lib/bookings';
import { bookingsQueryKeys } from 'src/features/bookings/api/query-keys/bookings';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';
import { fetchEmployees } from 'src/features/employees/api/lib/employees';
import { employeesKeys } from 'src/features/employees/api/query-keys/employees';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { Route } from 'src/routes/_layout/tenant/employees/groups/$groupToken/_layout/employees';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { EmployeesGroupDetailsEmployeesActionsTableMenu } from '../../molecules/employees-group-details/employees/employees-actions-table-menu';
import { AddEmployeesToGroupDialog } from '../../organisms/dialogs/employees-group-details/employees/add-employees-to-group-dialog';
import { EmployeeHasActiveOrFutureBookingsDialog } from '../../organisms/dialogs/employees-group-details/employees/employee-has-active-or-future-bookings-dialog';
import { RemoveEmployeesFromGroupDialog } from '../../organisms/dialogs/employees-group-details/employees/remove-employees-from-group-dialog';
import { EmployeesGroupDetailsEmployeesTable } from '../../organisms/employees-group-details/employees/employees-table';
import { EmployeesActionsSlotPayload } from '../../organisms/employees-group-details/employees/employees-table-row';

const EmployeesGroupDetailsEmployees = () => {
  const { t } = useTranslation(['employeesGroups', 'common']);

  const { directoryToken } = useUserStore();

  const { query, perPage, page } = useSearch({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/employees',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const { groupToken } = Route.useParams();

  const [sortController, setSortController] = useSortController({
    sortBy: 'email',
    sortOrder: SortOrder.DESC,
  });

  const { results: employeesGroup } = useEmployeesGroupsByTokens([groupToken]);

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
      filter: {
        employeeGroupToken: {
          value: groupToken,
        },
      },
    };
  }, [groupToken, sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeSort,
  } = useMeilisearchWithUrlParams(
    fetchEmployees,
    employeesKeys.detail(groupToken, directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeesActionsSlotPayload | null>(null);

  // Add employees to group
  const [
    isOpenAddEmployeesToGroupDialog,
    handleOpenAddEmployeesToGroupDialog,
    handleCloseAddEmployeesToGroupDialog,
  ] = useDialog();

  // Remove employee from group
  const [
    isOpenRemoveEmployeeFromGroupDialog,
    handleOpenRemoveEmployeeFromGroupDialog,
    handleCloseRemoveEmployeeFromGroupDialog,
  ] = useDialog();

  // Employee has active or future bookings
  const [
    isOpenEmployeeHasActiveOrFutureBookingsDialog,
    handleOpenEmployeeHasActiveOrFutureBookingsDialog,
    handleCloseEmployeeHasActiveOrFutureBookingsDialog,
  ] = useDialog();

  const { results: bookings } = useMeilisearch({
    fetch: fetchBookings,
    queryKey: bookingsQueryKeys.list(directoryToken ?? '', 1, 1000),
    page: 1,
    perPage: 1000,
    searchState: {
      filter: [
        results?.hits?.map(({ token }) => `employeeBookerToken = ${token}`) ??
          [],
        [`status = "BOOKED"`],
      ],
      sort: [],
      q: '',
    },
    enabled: Boolean(results?.hits && results?.hits?.length > 0),
  });

  const hasEmployeeActiveOrFutureBookings = (employeeToken: string) =>
    bookings?.hits.some(
      ({ employeeBookerToken }) => employeeBookerToken === employeeToken,
    );

  const actionsSlot = useCallback(
    ({ token }: EmployeesActionsSlotPayload) => (
      <EmployeesGroupDetailsEmployeesActionsTableMenu
        onRemove={() => {
          setSelectedEmployee({ token });

          if (hasEmployeeActiveOrFutureBookings(token)) {
            handleOpenEmployeeHasActiveOrFutureBookingsDialog();
            return;
          }

          handleOpenRemoveEmployeeFromGroupDialog();
        }}
      />
    ),
    [bookings],
  );

  const handleOnRemoveEmployeeCancel = () => {
    handleCloseRemoveEmployeeFromGroupDialog();
    setSelectedEmployee(null);
  };

  return (
    <>
      <PageHeader
        title={t('employeesGroups:employees.title')}
        headerVariant="h5"
        extras={
          <Can I="create" a="EmployeesGroupEmployees" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={handleOpenAddEmployeesToGroupDialog}
                    color="primary"
                    disabled={!allowed}
                    startIcon={<Plus fontSize="small" />}
                    variant="contained"
                    id="add_employees_button"
                    size="large">
                    {t('employeesGroups:employees.addFromExisting')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        }
      />

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />
        </Box>

        <EmployeesGroupDetailsEmployeesTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <AddEmployeesToGroupDialog
        open={isOpenAddEmployeesToGroupDialog}
        onCancel={handleCloseAddEmployeesToGroupDialog}
        employeeGroupToken={groupToken}
      />

      <RemoveEmployeesFromGroupDialog
        open={isOpenRemoveEmployeeFromGroupDialog}
        onCancel={handleOnRemoveEmployeeCancel}
        employeeGroupToken={groupToken}
        employeeToken={selectedEmployee?.token ?? ''}
        employeeGroupName={employeesGroup?.hits[0]?.name ?? ''}
      />

      <EmployeeHasActiveOrFutureBookingsDialog
        open={isOpenEmployeeHasActiveOrFutureBookingsDialog}
        onCancel={handleCloseEmployeeHasActiveOrFutureBookingsDialog}
        employeeGroupToken={groupToken}
        employeeToken={selectedEmployee?.token ?? ''}
        bookingsToManage={bookings?.hits ?? []}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'EmployeesGroupEmployees'),
  EmployeesGroupDetailsEmployees,
);
